import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Activity, Pause, Play, Trash2, WifiOff, Wifi, ChevronDown } from "lucide-react";
import { tokenStore } from "@/lib/api";

const API_URL  = process.env.REACT_APP_BACKEND_URL;
const WS_URL   = API_URL.replace(/^https?/, (m) => (m === "https" ? "wss" : "ws"));
const MAX_ROWS = 200;

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtBytes = (b) => b >= 1_073_741_824 ? `${(b/1_073_741_824).toFixed(1)} GB`
  : b >= 1_048_576 ? `${(b/1_048_576).toFixed(1)} MB`
  : b >= 1024 ? `${(b/1024).toFixed(1)} KB`
  : `${b} B`;

const fmtMs   = (ms) => ms < 1 ? `${(ms*1000).toFixed(0)}μs` : `${ms.toFixed(1)}ms`;
const fmtNum  = (n)  => n >= 1_000_000 ? `${(n/1_000_000).toFixed(2)}M`
  : n >= 1_000 ? `${(n/1_000).toFixed(1)}K` : String(n);

const endpointMeta = (ep = "") => {
  if (ep.includes("/vrf"))                                return { color: "amber",   label: "VRF" };
  if (ep.includes("/bytes"))                              return { color: "emerald", label: "Bytes" };
  if (ep.includes("/token"))                              return { color: "blue",    label: "Token" };
  if (ep.includes("/sequence") || ep.includes("/range")) return { color: "purple",  label: "Range" };
  if (ep.includes("/health") || ep.includes("/stats"))   return { color: "slate",   label: "Health" };
  return { color: "blue", label: "QRNG" };
};

const colorMap = {
  amber:   { border: "border-l-amber-400",   bg: "bg-amber-50/60",   badge: "bg-amber-100 text-amber-700",   dot: "bg-amber-400"   },
  emerald: { border: "border-l-emerald-400", bg: "bg-emerald-50/60", badge: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-400" },
  blue:    { border: "border-l-blue-400",    bg: "bg-blue-50/60",    badge: "bg-blue-100 text-blue-700",     dot: "bg-blue-400"    },
  purple:  { border: "border-l-purple-400",  bg: "bg-purple-50/60",  badge: "bg-purple-100 text-purple-700", dot: "bg-purple-400"  },
  slate:   { border: "border-l-slate-300",   bg: "",                 badge: "bg-slate-100 text-slate-600",   dot: "bg-slate-400"   },
};

const sourceBadge = (src = "") =>
  src.startsWith("QRNG") ? "bg-cyan-100 text-cyan-700"
  : src === "OS-CSPRNG-DEV" ? "bg-amber-100 text-amber-700"
  : "bg-slate-100 text-slate-500";

// ── Sub-components ────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, highlight }) {
  return (
    <div className={`flex-1 min-w-0 border bg-white p-4 transition-all ${highlight ? "border-cyan-300 shadow-sm" : "border-slate-200"}`}
         data-testid={`kpi-${label.toLowerCase().replace(/\s+/g,"-")}`}>
      <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">{label}</p>
      <p className={`text-2xl font-bold font-mono leading-none ${highlight ? "text-cyan-600" : "text-slate-900"}`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-1 font-mono">{sub}</p>}
    </div>
  );
}

function EndpointBar({ rows }) {
  const counts = useMemo(() => {
    const c = {};
    rows.forEach(r => {
      const { label } = endpointMeta(r.endpoint);
      c[label] = (c[label] || 0) + 1;
    });
    return Object.entries(c).sort((a,b) => b[1]-a[1]);
  }, [rows]);

  const total = counts.reduce((s, [,v]) => s + v, 0) || 1;
  const barColors = { VRF:"bg-amber-400", Bytes:"bg-emerald-400", Token:"bg-blue-400", Range:"bg-purple-400", Health:"bg-slate-300", QRNG:"bg-blue-400" };
  const textColors = { VRF:"text-amber-700", Bytes:"text-emerald-700", Token:"text-blue-700", Range:"text-purple-700", Health:"text-slate-500", QRNG:"text-blue-700" };

  return (
    <div className="flex items-center gap-3 flex-wrap" data-testid="endpoint-distribution">
      {counts.map(([label, count]) => {
        const pct = Math.round((count/total)*100);
        return (
          <div key={label} className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full shrink-0 ${barColors[label] || "bg-slate-400"}`} />
            <span className={`text-xs font-medium ${textColors[label] || "text-slate-600"}`}>{label}</span>
            <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-500 ${barColors[label] || "bg-slate-400"}`}
                   style={{ width: `${pct}%` }} />
            </div>
            <span className="text-[10px] text-slate-400 font-mono w-7">{pct}%</span>
          </div>
        );
      })}
    </div>
  );
}

function CallRow({ row, isNew }) {
  const { color } = endpointMeta(row.endpoint);
  const c = colorMap[color];
  const timeStr = new Date(row.timestamp).toLocaleTimeString("en-US", {
    hour12: false, hour:"2-digit", minute:"2-digit", second:"2-digit",
  }) + "." + String(new Date(row.timestamp).getMilliseconds()).padStart(3,"0").slice(0,1);

  return (
    <div
      className={`grid grid-cols-[90px_70px_1fr_90px_70px_65px_120px] gap-0 border-b border-slate-100 border-l-2 text-[11px] transition-all duration-300
        ${c.border} ${isNew ? "animate-row-flash" : ""} hover:bg-slate-50/80`}
      data-testid="call-row"
    >
      <div className="px-3 py-2.5 font-mono text-slate-400 whitespace-nowrap">{timeStr}</div>
      <div className="px-2 py-2.5 font-bold text-slate-500">{row.method}</div>
      <div className="px-2 py-2.5 font-mono text-slate-800 truncate">{row.endpoint}</div>
      <div className="px-2 py-2.5 font-mono text-right text-slate-600">{fmtBytes(row.bytes)}</div>
      <div className={`px-2 py-2.5 font-mono text-right font-semibold
        ${row.latency_ms < 2 ? "text-emerald-600" : row.latency_ms < 5 ? "text-blue-600" : "text-amber-600"}`}>
        {fmtMs(row.latency_ms)}
      </div>
      <div className={`px-2 py-2.5 text-center font-semibold
        ${row.status === 200 ? "text-emerald-600" : "text-red-500"}`}>
        {row.status}
      </div>
      <div className={`px-2 py-2.5`}>
        <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide ${sourceBadge(row.source || "QRNG-DRBG")}`}>
          {(row.source || "QRNG-DRBG").replace("QRNG-AES-256-CTR-DRBG","QRNG-DRBG")}
        </span>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function LiveApiMonitor() {
  const [rows, setRows]               = useState([]);
  const [newIds, setNewIds]           = useState(new Set());
  const [paused, setPaused]           = useState(false);
  const [autoScroll, setAutoScroll]   = useState(true);
  const [connected, setConnected]     = useState(false);
  const [kpis, setKpis]               = useState({ total_calls: 0, total_bytes: 0, avg_latency_ms: 0 });
  const [callsPerSec, setCallsPerSec] = useState(0);
  const [epFilter, setEpFilter]       = useState("all");
  const [srcFilter, setSrcFilter]     = useState("all");

  const wsRef         = useRef(null);
  const pauseRef      = useRef(false);
  const bufferRef     = useRef([]);
  const tableRef      = useRef(null);
  const callTsRef     = useRef([]);   // timestamps for calls/sec calculation
  const flushRef      = useRef(null);

  // Flash animation cleanup
  const clearFlash = useCallback((id) => {
    setTimeout(() => setNewIds(p => { const n = new Set(p); n.delete(id); return n; }), 600);
  }, []);

  // Flush buffer into state at 60fps
  const flush = useCallback(() => {
    if (!bufferRef.current.length) return;
    const incoming = bufferRef.current.splice(0);
    setRows(prev => {
      const combined = [...incoming, ...prev].slice(0, MAX_ROWS);
      return combined;
    });
    const ids = new Set(incoming.map(r => r._id));
    setNewIds(prev => new Set([...prev, ...ids]));
    incoming.forEach(r => clearFlash(r._id));

    if (autoScroll && tableRef.current) {
      tableRef.current.scrollTop = 0;
    }
  }, [autoScroll, clearFlash]);

  useEffect(() => {
    flushRef.current = setInterval(flush, 60);
    return () => clearInterval(flushRef.current);
  }, [flush]);

  // Calls/sec calculation from rolling window
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      callTsRef.current = callTsRef.current.filter(t => now - t < 5000);
      setCallsPerSec(Math.round(callTsRef.current.length / 5));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // WebSocket connection
  useEffect(() => {
    const token = tokenStore.getAccess();
    const ws = new WebSocket(`${WS_URL}/api/ws/feed?token=${token}`);
    wsRef.current = ws;

    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onerror = () => setConnected(false);

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === "connected" || msg.type === "stats") {
          setKpis(prev => ({ ...prev, ...msg.data }));
        }
        if (msg.type === "call_event") {
          if (pauseRef.current) return;
          const row = { ...msg.data, _id: `${msg.data.request_id}-${Date.now()}` };
          bufferRef.current.unshift(row);
          callTsRef.current.push(Date.now());
        }
      } catch {}
    };

    const ping = setInterval(() => ws.readyState === 1 && ws.send("ping"), 20000);
    return () => { clearInterval(ping); ws.close(); };
  }, []);

  const togglePause = () => {
    pauseRef.current = !pauseRef.current;
    setPaused(pauseRef.current);
  };

  const handleClear = () => { setRows([]); bufferRef.current = []; };

  // Active tenants from last 50 rows
  const activeTenants = useMemo(() =>
    new Set(rows.slice(0, 50).map(r => r.tenant).filter(Boolean)).size,
  [rows]);

  // Filtered rows
  const filtered = useMemo(() => rows.filter(r => {
    if (epFilter !== "all" && !r.endpoint?.includes(epFilter)) return false;
    if (srcFilter !== "all" && r.status >= 400 !== (srcFilter === "errors")) return false;
    return true;
  }), [rows, epFilter, srcFilter]);

  const endpointOptions = [
    { value: "all",       label: "All Endpoints" },
    { value: "/token",    label: "/token" },
    { value: "/bytes",    label: "/bytes" },
    { value: "/vrf",      label: "/vrf" },
    { value: "/sequence", label: "/sequence" },
    { value: "/range",    label: "/range" },
    { value: "/integer",  label: "/integer" },
    { value: "/float",    label: "/float" },
    { value: "/uuid",     label: "/uuid" },
  ];

  return (
    <div className="flex flex-col h-full" data-testid="live-api-monitor">

      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <Activity className="w-6 h-6 text-cyan-500" />
            Live API Monitor
            <span className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border
              ${connected ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-600 border-red-200"}`}
              data-testid="ws-status-badge">
              <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-emerald-500 animate-pulse" : "bg-red-500"}`} />
              {connected ? "LIVE" : "DISCONNECTED"}
            </span>
          </h1>
          <p className="text-sm text-slate-400 mt-0.5 ml-9">Real-time API calls from all tenants — every call is shown as it happens</p>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={() => setAutoScroll(p => !p)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors
              ${autoScroll ? "bg-cyan-50 border-cyan-200 text-cyan-700" : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"}`}
            data-testid="autoscroll-btn">
            <ChevronDown className="w-3.5 h-3.5" />
            Auto-scroll {autoScroll ? "ON" : "OFF"}
          </button>
          <button onClick={togglePause}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border rounded-lg transition-colors
              ${paused ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}
            data-testid="pause-btn">
            {paused ? <><Play className="w-3.5 h-3.5" />Resume</> : <><Pause className="w-3.5 h-3.5" />Pause</>}
          </button>
          <button onClick={handleClear}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-slate-200 text-slate-500 rounded-lg hover:bg-slate-50 transition-colors"
            data-testid="clear-btn">
            <Trash2 className="w-3.5 h-3.5" />Clear
          </button>
        </div>
      </div>

      {/* ── KPI Row ── */}
      <div className="flex gap-3 mb-4" data-testid="kpi-row">
        <KpiCard label="Total Calls"     value={fmtNum(kpis.total_calls)}  sub="since startup" highlight />
        <KpiCard label="Bytes Served"    value={fmtBytes(kpis.total_bytes)} sub="cumulative" />
        <KpiCard label="Avg Latency"     value={fmtMs(kpis.avg_latency_ms)} sub="rolling avg" />
        <KpiCard label="Active Tenants"  value={activeTenants}  sub="last 50 calls" />
        <KpiCard label="Calls / sec"     value={callsPerSec}    sub="5s window" />
      </div>

      {/* ── Endpoint distribution ── */}
      <div className="bg-white border border-slate-200 px-4 py-3 mb-4 flex items-center gap-4">
        <span className="text-[10px] uppercase tracking-widest text-slate-400 shrink-0">Distribution</span>
        <EndpointBar rows={rows} />
      </div>

      {/* ── Filters ── */}
      <div className="flex items-center gap-3 mb-3" data-testid="filter-bar">
        <select value={epFilter} onChange={e => setEpFilter(e.target.value)}
          className="text-xs border border-slate-200 bg-white px-3 py-1.5 rounded-lg text-slate-600 focus:outline-none focus:border-cyan-400 cursor-pointer"
          data-testid="endpoint-filter">
          {endpointOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={srcFilter} onChange={e => setSrcFilter(e.target.value)}
          className="text-xs border border-slate-200 bg-white px-3 py-1.5 rounded-lg text-slate-600 focus:outline-none focus:border-cyan-400 cursor-pointer"
          data-testid="source-filter">
          <option value="all">All Status</option>
          <option value="ok">200 OK only</option>
          <option value="errors">Errors only</option>
        </select>
        <span className="text-[11px] text-slate-400 ml-auto font-mono" data-testid="row-count">
          {filtered.length} / {MAX_ROWS} rows
        </span>
        {paused && (
          <span className="text-[11px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full animate-pulse"
            data-testid="paused-badge">
            ⏸ PAUSED — {bufferRef.current.length} events buffered
          </span>
        )}
      </div>

      {/* ── Table ── */}
      <div className="flex-1 border border-slate-200 bg-white overflow-hidden flex flex-col min-h-0">
        {/* Header */}
        <div className="grid grid-cols-[90px_70px_1fr_90px_70px_65px_120px] bg-slate-50 border-b-2 border-slate-200 shrink-0">
          {["Time", "Method", "Endpoint", "Bytes", "Latency", "Status", "Source"].map(h => (
            <div key={h} className="px-3 py-2.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">{h}</div>
          ))}
        </div>

        {/* Rows */}
        <div ref={tableRef} className="overflow-y-auto flex-1" data-testid="call-table">
          {!connected && rows.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400 gap-3">
              <WifiOff className="w-10 h-10 opacity-30" />
              <p className="text-sm">Connecting to live feed…</p>
            </div>
          )}
          {connected && rows.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400 gap-3">
              <Wifi className="w-8 h-8 opacity-40 animate-pulse" />
              <p className="text-sm">Waiting for API calls…</p>
            </div>
          )}
          {filtered.map(row => (
            <CallRow key={row._id} row={row} isNew={newIds.has(row._id)} />
          ))}
        </div>
      </div>

      {/* Row-flash keyframe via inline style */}
      <style>{`
        @keyframes rowFlash {
          0%   { background-color: #fef9c3; }
          100% { background-color: transparent; }
        }
        .animate-row-flash { animation: rowFlash 0.6s ease-out forwards; }
      `}</style>
    </div>
  );
}
